import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  BackArrow,
  Input,
  Select,
  Button,
  RightInformation,
  TopProgress,
  RadioButtonGroup,
  Typography
} from '../../../../components'
import {
  Container,
  TitleBig,
  CreateContainer,
} from './styles'
import * as yup from 'yup';
import { useFormik } from 'formik';
import { securityService, clientService } from '../../../../api'
import { useUi } from '../../../../context/ui.context'
import { usePageTitle } from '../../../../context/navigationContext'



const CreateSecurityGroup:FC = () => {
  const navigate = useNavigate()
  const [t] = useTranslation()
  const {viewAlert} = useUi()
  const {groupId} = useParams()
  usePageTitle(t("PAGE_TITLE_SECURITY_GROUP_CREATE"))
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingComplete, setLoadingComplete] = useState<boolean>(false)
  const [regions, setRegions] = useState<any[]>([])
  const [securityGroup, setSecurityGroup] = useState<any>()

  // Staff/Client project selection
  const [createInClientProject, setCreateInClientProject] = useState<boolean>(true)
  const [selectedClient, setSelectedClient] = useState<any>(null)
  const [clients, setClients] = useState<any[]>([])
  const [clientsLoading, setClientsLoading] = useState<boolean>(false)
  const [clientSearchString, setClientSearchString] = useState<string>('')

  const [initialValues, setInitialValues ] = useState<any>({
    name:'',
    region:'',
    description:''
  })
  const onSubmit = (values:any) => {
    // Validate client selection if creating in client project
    if (!groupId && createInClientProject && !selectedClient) {
      viewAlert({
        severity:'error',
        message:t('PLEASE_SELECT_CLIENT'),
      })
      return;
    }

    setLoading(true)
    const data:any = {
      name:values.name,
      region:values.region,
      description:values.description,
    }

    // Add client context for new security groups
    if (!groupId) {
      if (createInClientProject) {
        // Creating in client's project
        data.client = selectedClient;
      } else {
        // Creating in admin project
        data.client = null;
      }
    }

    if(groupId) {
      securityService.editGroup(groupId, data)
        .then(() => {
          setPageLoading(true);
          setTimeout(() => {
            setPageLoading(false);
            setLoading(false)
            navigate("/openstack/security-groups")
          },1000)
        })
        .catch((e:any) => {
          if(e?.response?.data.detail){
            viewAlert({
              severity:'error',
              message:e.response.data.detail,
            })
          } else if(e?.response?.data) {
            setErrors(e.response.data)
          } else {
            viewAlert({
              severity:'error',
              message:`${t("ERROR_OCCURRED")}`,
            })
          }
        })
        .finally(() => {
          setLoading(false)
        })
    } else {
      securityService.createGroup(data)
      .then((res:any) => {
        setPageLoading(true);
        setTimeout(() => {
          setPageLoading(false);
          navigate("/openstack/security-groups")
        },1000)
      })
      .catch((e:any) => {
        if(e?.response?.data.detail){
          viewAlert({
            severity:'error',
            message:e.response.data.detail,
          })
        } else if(e?.response?.data) {
          setErrors(e.response.data)
        } else {
          viewAlert({
            severity:'error',
            message:`${t("ERROR_OCCURRED")}`,
          })
        }
      })
      .finally(() => {
        setLoading(false)
      })
    }
  }
  const regName = /^[a-zA-Z0-9\\!\"#$%&'()*+,\-./:;<=>?@\[\\\]\^_`{\|}~ ]+$/
  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleChange,
    setErrors,
    handleBlur,
  } = useFormik({
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      name: yup
        .string()
        .required(`${t('REQUIRED_FIELD')}`)
        .matches(regName,`${t("ONLY_ENGLISH_LETTERS")}`)
        .max(60, `${t("STRING_VALIDATE_MAX")} 60 ${t("CHARACTERS")}`),
      region: yup.string().required(`${t('REQUIRED_FIELD')}`),
      description: yup.string().max(255, `${t("STRING_VALIDATE_MAX")} 255 ${t("CHARACTERS")}`),
    }),
    initialValues: {
      ...initialValues,
    },
    onSubmit,
  });


  // Load clients when switching to client project mode
  useEffect(() => {
    if (createInClientProject && !groupId) {
      loadClients();
    }
  }, [createInClientProject, clientSearchString]);

  const loadClients = () => {
    setClientsLoading(true);
    clientService.getAll({
      page: 1,
      page_size: 100,
      search: clientSearchString,
      has_enabled_project: true
    })
    .then((res) => {
      const clientsData = res.data?.objects || [];
      const formattedClients = clientsData.map((client: any) => ({
        id: client.id,
        value: client.id,
        label: `${client.first_name} ${client.last_name} (${client.email})`,
        data: client
      }));
      setClients(formattedClients);
    })
    .catch((error) => {
      console.error("Failed to load clients", error);
    })
    .finally(() => {
      setClientsLoading(false);
    });
  };

  const handleDestinationChange = (value: boolean) => {
    setCreateInClientProject(value);
    if (!value) {
      // Reset client selection when switching to admin project
      setSelectedClient(null);
    }
  };

  useEffect(() => {
    securityService.getCreateOptions()
    .then((res:any) => {
      setRegions(res.data.regions.map((reg:any) => {
        return{
          value:reg.id,
          label:reg.id
        }
      }))
      if(groupId){
        securityService.getGroupById(groupId)
        .then((res) => {
          setSecurityGroup(res.data)
          setPageLoading(false)
          setInitialValues({
            name:res.data.name,
            region:res.data.region,
            description:res.data.description,
          })
        })
        .catch((err) => {
          viewAlert({
            severity:"error",
            message:`${t("UNABLE_TO_FIND_SECURITY_GROUP")}`
          })
          navigate("/openstack/security-groups")
        })
      } else {
        setPageLoading(false)
        handleChange('region')(res.data.regions?.[0]?.id)
      }

    })
    .catch((e:any) => {
      console.log('err get create options', e);
    })
  },[])

  
  const formikProps = {
    errors,
    values,
    touched,
    handleChange,
    handleBlur,
  };
  if(pageLoading) return <TopProgress loading={pageLoading} />
  return(
    <CreateContainer>
      <Container>
        <BackArrow fallbackPath="/openstack/security-groups"/>
        <TitleBig>{groupId ? `${t("EDIT")} ${securityGroup.name}` : t("CREATE_A_NEW_SECURITY_GROUP")}</TitleBig>
        <div style={{display:'flex', flexDirection:'column', gap:'20px'}}>
          {/* Staff/Client Project Selection - only for new security groups */}
          {!groupId && (
            <>
              <div style={{ marginBottom: '16px' }}>
                <Typography variant="h4" style={{ marginBottom: '12px' }}>
                  {t("PROJECT_SELECTION")}
                </Typography>
                <RadioButtonGroup
                  title=""
                  buttons={[
                    {
                      id: 'admin',
                      title: t("CREATE_SECURITY_GROUP_IN_ADMIN_PROJECT"),
                      value: 'admin'
                    },
                    {
                      id: 'client',
                      title: t("CREATE_SECURITY_GROUP_IN_CLIENT_PROJECT"),
                      value: 'client'
                    }
                  ]}
                  selectedButton={createInClientProject ? 'client' : 'admin'}
                  onChange={(value: string) => handleDestinationChange(value === 'client')}
                />
              </div>

              {createInClientProject && (
                <Select
                  required
                  toolTip={false}
                  data={clients}
                  selectedValue={selectedClient}
                  onChange={(id: any) => setSelectedClient(id)}
                  title={t("CLIENT")}
                  placeholder={t("SELECT_CLIENT")}
                  fieldName='client'
                  hasSearch={true}
                  onSearchChange={setClientSearchString}
                  customStyles={{marginBottom:'16px'}}
                />
              )}
            </>
          )}

          <Input
            fieldName='name'
            title={t("NAME")}
            customStyles={{width:'360px'}}
            placeholder=''
            {...formikProps}
          />
          {/* <Select
            toolTip={false}
            data={regions}
            selectedValue={values['region']}
            onChange={(reg) => handleChange('region')(reg)}
            title={t("PLACEMENT_REGION")}
            customStyles={{width:'360px'}}
          /> */}
          <Input
            isTextArea
            fieldName='description'
            title={t("DESCRIPTION")}
            customStyles={{minWidth:'550px', maxWidth:'550px', height:'200px'}}
            placeholder=''
            {...formikProps}
          />
        </div>
        <div style={{display:'flex', marginTop:'40px'}}>
          <Button
            variant="primary"
            size="display"
            customStyles={{marginRight:"10px"}}
            onClick={handleSubmit}
            loading={loading}
            loadingComplete={loadingComplete}
            title={t("CREATE")}
          />
          <Button
            variant="stroke"
            size="display"
            onClick={() => navigate("/openstack/security-groups")}
            title={t("CANCEL")}
          />
        </div>
      </Container>
      <RightInformation
        customStyles={{marginTop:'101px'}}
        info={t("INFO_CREATE_SECURITY_GROUP")}
      />
    </CreateContainer>
  )
}

export default CreateSecurityGroup
